import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  CreateDrumsetCommand,
  DeleteDrumsetCommand,
  DuplicateDrumsetCommand,
  RenameDrumsetCommand,
  SortDrumsetsAlphabeticallyCommand,
  SortDrumsetsCommand,
  MarkDrumsetAsOfficialCommand,
} from 'editor/models/drumset-commands';
import {
  AddFolderCommand,
  MoveDrumCommand,
  SortFoldersCommand,
  SortFoldersSongsAlphabeticallyCommand,
  SortDrumsetsFolderCommand,
  SortDrumFoldersCommand,
  RenameFolderCommand,
  DeleteSongFolderCommand,
} from 'editor/models/folder-commands';
import type ProjectManagerService from 'editor/services/project-manager';
import type { DrumsetFile, SongFolder } from 'editor/services/project-manager';
import type UndoManagerService from 'editor/services/undo-manager';
import nameFilter from 'editor/utils/name-filter';
import wait from 'editor/utils/wait';
import {lastChangeWasMarkOfficialState} from 'editor/models/drumset';

export default class RestrictedDrumsetsController extends Controller {
  @service declare router: RouterService;
  @service declare projectManager: ProjectManagerService;
  @service declare undoManager: UndoManagerService;

  // nameFilter = nameFilter;
  @tracked menuDrumset?: DrumsetFile | SongFolder;
  @tracked ignoreNextClickOutside: boolean = false;

  get folder() {
    return this.projectManager.currentFolder;
  }

  get folders() {
    return this.projectManager.drumFolders;
  }

  get drumsets() {
    return this.projectManager.drumsOnTheLoose;
  }

  get selectedDrumset() {
    return this.projectManager.currentDrumset;
  }

  get currentProject() {
    return this.projectManager.currentProject!;
  }

  get isCurrentDrumsetDRMX() {
    return this.projectManager.isCurrentDrumsetDRMX();
  }

  get isProjectDRMX() {
    return this.projectManager.isProjectDRMX;
  }

  get isProjectDevMode() {
    return this.projectManager.isProjectDevMode;
  }

  @action
  isOfficial(drumsetFile: DrumsetFile) {
    if (drumsetFile.drumset) {
      return (drumsetFile.drumset.lastChangeWasMarkOfficial !== lastChangeWasMarkOfficialState.False && !drumsetFile.drumset.instruments.some((i) => i.isDirty)) || (drumsetFile.drumset.header.type == 'BBsg' 
      && ((!drumsetFile.isDirty && !drumsetFile.drumset.isDirty)  // if the drum set is dirty, it's marked unofficial
      || !this.projectManager.isDirty)); // but if the project isn't dirty, you're back at the start / last save point,
    }                                 // at which point drumsetFile.drumset.header.type should be accurate, so if the drumset was official at that point, it's still official    
                                      // existing bug is if you have an official drum set, make a change to it, and undo, if you make the project dirty in any way (even leaving that drum set alone) it shows it as unofficial, but that's purely a visual glitch, it is truly official. Reason is, a save and undo will leave that drum set as dirty - we know the project is clean when there's nothing left to undo (you're at the point you started at), but we don't have that level of tracking for each object. So if you have a clean project, change a drum set, and undo, the project is clean, but the drum set is "dirty" despite being at its starting point too.
    return drumsetFile.isOfficial && (!drumsetFile.isDirty || !this.projectManager.isDirty);
  }


  @action
  showMenuFor(drumset: DrumsetFile | SongFolder | undefined, ev?: MouseEvent) {
    if (!drumset && this.ignoreNextClickOutside) {
      this.ignoreNextClickOutside = false;
      return;
    }
    if (this.menuDrumset != drumset) {
      ev?.preventDefault();
      ev?.stopPropagation();
      if (this.menuDrumset && drumset) {
        this.ignoreNextClickOutside = true;
      }
      this.menuDrumset = drumset;
    }
    if (!drumset) {
      this.menuDrumset = undefined;
      return;
    }
  }

  @action
  shouldShowMenuFor(drumset: DrumsetFile | SongFolder) {
    return this.menuDrumset === drumset;
  }

  @action // for rearranging drumsets that are NOT in a folder
  sortDrumsets(oldIndex: number, newIndex: number) {
    this.undoManager.executeCommand(
      new SortDrumsetsCommand(this.projectManager, oldIndex, newIndex),
    );
  }

  @action
  dragDrumToFolder(fromElement: HTMLElement,
    fromIndex: number,
    _toElement: HTMLElement,
    toIndex: number,
  ) {
    // this is for moving a drumset from the main list (drumsOnTheLoose, outside any folders) to a folder

    // eslint-disable-next-line ember/no-runloop
    next(async () => {
      const folderFromIndex = parseInt(fromElement.dataset['folderIndex']!);
      const folderToIndex = parseInt(_toElement.dataset['folderIndex']!);
      const fromFolder = this.folders[folderFromIndex]!;
      const toFolder = this.folders[folderToIndex]!;
      // toFolder is null if moving to drumsOnTheLoose - duh, no folder
      this.undoManager.executeCommand(
        new MoveDrumCommand(fromFolder, fromIndex, toFolder, toIndex, this.projectManager),
      );
    });
  }

  @action
  sortDrumFolders(oldIndex: number, newIndex: number) {
    this.undoManager.executeCommand(
      new SortDrumFoldersCommand(this.projectManager, oldIndex, newIndex),
    );
  }

  @action // rearranging drumsets within a folder
  sortDrumsetsInFolder(folder: SongFolder, oldIndex: number, newIndex: number) {
    this.undoManager.executeCommand(
      new SortDrumsetsFolderCommand(folder, oldIndex, newIndex),
    );
  }

  @action
  renameDrumset(drumset: DrumsetFile, name: string) {
    if (name !== drumset.name) {
      this.undoManager.executeCommand(new RenameDrumsetCommand(drumset, name));
    }
  }

  @action
  selectDrumset(drumset: DrumsetFile) {
    this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
  }

  @action
  renameFolder(folder: SongFolder, name: string) {
    if (name !== folder.name) {
      this.projectManager.drumFoldersDirty = true;
      this.undoManager.executeCommand(new RenameFolderCommand(folder, name));
    }
  }

  @action
  handleSpacePress(folder: SongFolder, event) {
    if (event.key === " " || event.key === "Space") {
      event.preventDefault();
      const inputElement = event.target;
      if (inputElement) {
        const cursorPos = inputElement.selectionStart;
        const currentValue = inputElement.value;
        const newValue = currentValue.slice(0, cursorPos) + ' ' + currentValue.slice(cursorPos);
        inputElement.value = newValue;
        inputElement.setSelectionRange(cursorPos + 1, cursorPos + 1);
        inputElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  @action
  exportFolder(folder: SongFolder) {
    this.projectManager.exportFolder(folder, true);
  }

  @action
  async addFolder() {
    const folder = await this.undoManager.executeCommand(
      new AddFolderCommand(this.projectManager, "New Drum Folder", true),
    );
//    this.router.transitionTo('restricted.drumfolders.folder', folder.path);
  }

  @action
  addDrumset() {
    const command = new CreateDrumsetCommand(this.projectManager);
    const previousDrumset = this.projectManager.currentDrumset;
    command.on('execute', (drumset) =>
      this.router.transitionTo('restricted.drumsets.drumset', drumset.path),
    );
    command.on('reverse', () => {
      this.projectManager.currentDrumset = previousDrumset;
      this.router.transitionTo('restricted.drumsets.index');
    });
    this.undoManager.executeCommand(command);
  }

  @action
  moveDrum(
    fromElement: HTMLElement,
    fromIndex: number,
    toElement: HTMLElement,
    toIndex: number,
  ) {
    // this is for moving a drum from one folder to another OR drumsOnTheLoose - outside any folders
    // eslint-disable-next-line ember/no-runloop
    next(async () => {
      const folderFromIndex = parseInt(fromElement.dataset['folderIndex']!);
      const folderToIndex = parseInt(toElement.dataset['folderIndex']!);
      const fromFolder = this.folders[folderFromIndex]!;
      const toFolder = this.folders[folderToIndex]!;
      // toFolder is null if moving to drumsOnTheLoose - duh, no folder
      this.undoManager.executeCommand(
        new MoveDrumCommand(fromFolder, fromIndex, toFolder, toIndex, this.projectManager),
      );
    });
  }

  @action
  sortDrumsetsAlphabetically() {
    this.undoManager.executeCommand(
      new SortDrumsetsAlphabeticallyCommand(this.projectManager),
    );
  }

  @action
  deleteDrumset(drumset: DrumsetFile) {
    const command = new DeleteDrumsetCommand(this.projectManager, drumset);
    command.on('execute', () => {
      this.router.transitionTo('restricted.drumsets.index');
    });
    command.on('reverse', (drumset) => {
      this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
    });
    this.undoManager.executeCommand(command);
  }

  @action
  deleteFolder(folder: SongFolder) {
    const command = new DeleteSongFolderCommand(this.projectManager, folder, true);
    command.on('execute', () => {
      this.router.transitionTo('restricted.drumsets');
    });
    this.undoManager.executeCommand(command);
  }

  @action
  async duplicateDrumset(drumset: DrumsetFile, ev?: MouseEvent) {
    ev?.stopPropagation();
    await wait(async () => {
      const command = new DuplicateDrumsetCommand(this.projectManager, drumset, !drumset.drmInPath);
      command.on('execute', (drumset) => {
        this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
      });
      command.on('reverse', (drumset) => {
        this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
      });
      try {
        await this.undoManager.executeCommand(command);
      } catch (err: any) {
        alert(`Error duplicating drumset`);
        throw err;
      }
    });
  }

  @action
  async convertDrumset(drumset: DrumsetFile, ev?: MouseEvent) {
    ev?.stopPropagation();
    await wait(async () => {
      try {
        const command = new DuplicateDrumsetCommand(this.projectManager, drumset, true);
        command.on('execute', (drumset) => {
          this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
        });
        command.on('reverse', (drumset) => {
          this.router.transitionTo('restricted.drumsets.drumset', drumset.path);
        });
        try {
          await this.undoManager.executeCommand(command);
          //BB2 requires version 2, revision 0
          command.newDrumset.drumset.header.version = 2;
          command.newDrumset.drumset.header.revision = 0;
        } catch (err: any) {
          alert(`Error duplicating drumset`);
          throw err;
        }
      } catch (err: any) {
        alert(`Error converting drumset`);
        throw err;
      }
    });
  }

  @action
  exportDrumset(drumset: DrumsetFile) {
    this.projectManager.exportDrumset(drumset);
  }

  @action
  async markDrumsetAsOfficial(drumsetFile: DrumsetFile) {
    if (!drumsetFile.drumset) {
      await drumsetFile.parse();
    }
    if (this.projectManager.isDirty && (drumsetFile.isDirty || drumsetFile.drumset?.isDirty)) {
      alert("This drum set has unsaved changes. Please save your project before marking a drumset as official.\n\nAs a reminder, any changes to the drumset will mark it as unofficial, so mark as official when the drum set is in its final form.");
      return;
    }
    const command = new MarkDrumsetAsOfficialCommand(drumsetFile);
    this.undoManager.executeCommand(command);
  }
}

